import { useCurrentDate, Button, H5, InputNumber } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { validateUpsWeight } from "@sellar-io/common";
import { ReactElement, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useCreateShipmentContext } from "../../../hooks/useCreateShipmentContext.shared";

import { Alert } from "../../alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../modal/Dialog";
import { getItemWeight } from "@sellar-io/common";
import { UPS_WEIGHT_CHANGE_DATE } from "../../../constants/sellarConstants.shared";

export function OrderAddBoxModal({
  isUnboxed,
  active,
  onDismiss,
  fulfillmentType,
  dispatchPlannedAt,
  onConfirm,
}: {
  isUnboxed: boolean;
  active: boolean;
  onDismiss: () => void;
  fulfillmentType: OrderGetResponseType["fulfillmentType"];
  dispatchPlannedAt?: string | Date | null;
  onConfirm: ({
    selectedLabelOrderItemIds,
    weight,
  }: {
    selectedLabelOrderItemIds: number[];
    weight: number;
  }) => void;
}): ReactElement {
  const currentDate = useCurrentDate();
  const [totalWeight, setTotalWeight] = useState<number>(0);
  const [selectedLabelOrderItemIds, setSelectedOrderItemIds] = useState<
    number[]
  >([]);
  const { boxes, items } = useCreateShipmentContext();

  // Filter out order items that are already in boxes (derive during render).
  const orderItems = useMemo(() => {
    if (!items) return undefined;
    return items.filter(
      (item) => !boxes.find((box) => box.OrderItemIds.includes(item.itemId)),
    );
  }, [boxes, items]);

  const comparisonDate = dispatchPlannedAt
    ? new Date(dispatchPlannedAt)
    : currentDate;

  const upsWeightValidation = validateUpsWeight(
    orderItems || [],
    UPS_WEIGHT_CHANGE_DATE,
    comparisonDate,
  );

  // Check if UPS weight validation should prevent submission
  const weightLimit = fulfillmentType === "ups" ? 25 : 32;

  return (
    <Dialog active={active} onDismiss={onDismiss} fullHeight={true}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={`Create label`}
        description={
          isUnboxed
            ? ""
            : "Where previously agreed, you can combine 1 or more order items into a single boxed item to produce a single label."
        }
      />
      <DialogContent className={"flex flex-col gap-4"}>
        {totalWeight > weightLimit && (
          <div className="mb-4">
            <Alert
              type="error"
              message={`The total weight of the items in this order exceeds the maximum allowed weight for UPS shipments ${
                upsWeightValidation
                  ? `(${upsWeightValidation.maxAllowedWeight}kg).`
                  : "."
              } Please adjust the order items or change the fulfillment type.`}
            />
          </div>
        )}
        <div
          className={
            "mb-0 max-h-72 divide-y overflow-y-scroll rounded-lg border border-solid pb-0"
          }
        >
          {orderItems?.map((item) => (
            <div
              key={item.itemId}
              className={twMerge(
                "flex flex-row justify-between p-4",
                selectedLabelOrderItemIds.includes(item.itemId) &&
                  "bg-gray-100",
              )}
              onClick={() => {
                const itemWeight = getItemWeight(item.Product);

                const isOrderItemIdAlreadySelected =
                  selectedLabelOrderItemIds.includes(item.itemId);

                // remove order item id from selected list

                if (isOrderItemIdAlreadySelected) {
                  setSelectedOrderItemIds(
                    selectedLabelOrderItemIds.filter(
                      (selectedGroup) => selectedGroup !== item.itemId,
                    ),
                  );
                  setTotalWeight((currentTotal) =>
                    currentTotal - itemWeight < 0
                      ? 0
                      : currentTotal - itemWeight,
                  );
                  return;
                }

                // add order item to selected list

                setSelectedOrderItemIds([
                  ...selectedLabelOrderItemIds,
                  item.itemId,
                ]);

                setTotalWeight((currentTotal) => currentTotal + itemWeight);
              }}
            >
              <div className={"flex flex-col"}>
                <H5>{item.Product.name}</H5>
              </div>
              <input
                checked={!!selectedLabelOrderItemIds.includes(item.itemId)}
                readOnly
                type={"checkbox"}
                className={"m-0 p-0"}
              />
            </div>
          ))}
        </div>
        <InputLabel>Total weight (kg)</InputLabel>
        <InputContainer>
          <InputNumber
            step={".01"}
            required={true}
            value={totalWeight}
            max={weightLimit}
            onChange={(e) => setTotalWeight(parseFloat(e.target.value))}
          />
        </InputContainer>
        {selectedLabelOrderItemIds && selectedLabelOrderItemIds.length > 1 ? (
          <Alert
            className={"w-full"}
            type={"information"}
            title={"Only 1 shipping label will be generated."}
            message={
              "Add items individually unless you intend to combine items into a single box."
            }
          />
        ) : null}
      </DialogContent>
      <DialogCTA>
        <Button
          disabled={
            !totalWeight ||
            !selectedLabelOrderItemIds.length ||
            totalWeight > weightLimit
          }
          onClick={() => {
            if (!selectedLabelOrderItemIds.length || !totalWeight) return;
            onConfirm({
              selectedLabelOrderItemIds,
              weight: totalWeight,
            });
            setSelectedOrderItemIds([]);
            setTotalWeight(0);
            onDismiss();
          }}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
}
